import type { Schedule } from "@/features/schedule/business-hours-form"
import { formatMoney } from "@/lib/formatters"
import { t } from "@/lib/i18n"

/**
 * Shape, endpoints and copy lookup for the standing price rules a restaurant
 * applies on its own.
 *
 * **The browser decides nothing about a promotion.** Whether a rule is live
 * right now depends on its campaign dates, its weekly window and the
 * restaurant's own time zone, and `Location::Promotion#live_at?` resolves all
 * three server-side. `liveNow` arrives already answered. Recomputing it here is
 * how the configuration screen and the till end up disagreeing about a rule a
 * diner is standing in front of.
 *
 * ## Two kinds, and neither of them is a courtesy
 *
 * `Location::PromotionPolicy::KINDS` is `percentage` and `fixed_amount` — no
 * `courtesy`. A courtesy is the whole line by definition, and a rule that could
 * hand a whole line away, repeatedly, with nobody's name on it is the shrinkage
 * this feature exists to avoid. The form has no third option to hide.
 *
 * ## `value` travels in the operator's own units
 *
 * A plain percentage for `percentage`, **pesos** for `fixed_amount` — the same
 * convention `features/staff/discounts.ts` documents, and the server converts at
 * its own boundary. `kind` is what says which; nothing here guesses from the
 * magnitude.
 *
 * ## There is no combo
 *
 * A scope is the whole bill, a set of dishes, or a set of categories. An order
 * line holds exactly one item and an item cannot contain items, so a bundle has
 * no row shape in this schema. If a fourth scope ever appears here it is a
 * different feature, not a fourth option.
 */

export type PromotionKind = "percentage" | "fixed_amount"

export type PromotionScope = "bill" | "items" | "categories"

/**
 * `draft` changes no price — it is where a rule is written and reviewed.
 * `active` is the only status the evaluation service walks. `archived` is where
 * a rule that already produced money goes, because it can never be destroyed:
 * a system-filed discount names its rule forever.
 */
export type PromotionStatus = "draft" | "active" | "archived"

export type CouponStatus = "active" | "exhausted" | "expired" | "revoked"

export type PromotionTarget = {
  id: number
  targetableType: string
  targetableId: number
  name: string | null
}

export type Coupon = {
  id: number
  code: string
  status: CouponStatus
  statusLabel: string
  maxRedemptions: number
  redemptionCount: number
  expiresAt: string | null
  revokedAt: string | null
  revocationReason: string | null
  /** False once a diner has redeemed it: the ledger keeps naming what they got. */
  destroyable: boolean
}

export type Promotion = {
  id: number
  name: string
  status: PromotionStatus
  statusLabel: string
  kind: PromotionKind
  kindLabel: string
  scope: PromotionScope
  scopeLabel: string
  priority: number
  /** Exact decimal string: a percentage, or a peso amount. `kind` says which. */
  value: string
  /** Peso ceiling on one application, or `null` for no ceiling. */
  maxDiscountAmount: string | null
  startsOn: string | null
  endsOn: string | null
  /** Answered by the server against the restaurant's zone, never recomputed. */
  liveNow: boolean
  /** The restaurant's zone, not the schedule row's. See `promotion-schedule`. */
  timeZone: string | null
  window: Schedule
  targetIds: number[]
  targets: PromotionTarget[]
  coupons: Coupon[]
}

export type PromotionOption = {
  value: string
  label: string
}

export type MenuItemOption = {
  id: number
  name: string
  categoryName: string | null
}

export type MenuCategoryOption = {
  id: number
  name: string
}

/**
 * Under `/manage`, which is the opposite of where the discount routes went. A
 * discount happens at the table in the middle of service; a promotion is
 * nothing but configuration.
 *
 * Coupons and the window are both nested under the rule they belong to, because
 * neither means anything on its own: a coupon does not define money, it points
 * at the promotion that does.
 */
export const PROMOTION_PATHS = {
  index: "/manage/promotions",
  create: "/manage/promotions",
  update: (promotionId: number) => `/manage/promotions/${promotionId}`,
  destroy: (promotionId: number) => `/manage/promotions/${promotionId}`,
  schedule: (promotionId: number) => `/manage/promotions/${promotionId}/schedule`,
  createCoupon: (promotionId: number) => `/manage/promotions/${promotionId}/coupons`,
  updateCoupon: (promotionId: number, couponId: number) =>
    `/manage/promotions/${promotionId}/coupons/${couponId}`,
  revokeCoupon: (promotionId: number, couponId: number) =>
    `/manage/promotions/${promotionId}/coupons/${couponId}/revoke`,
  destroyCoupon: (promotionId: number, couponId: number) =>
    `/manage/promotions/${promotionId}/coupons/${couponId}`,
} as const

/** The location feature that has to be on for any of this to answer at all. */
export const PROMOTIONS_FEATURE = "promotions"

/** The one scope that holds no targets: it reaches everything by definition. */
export const BILL_SCOPE: PromotionScope = "bill"

const I18N_PREFIX = "ui.react.features.staff.promotions"

export function tPromotion(key: string, options: Record<string, string | number> = {}) {
  return t(`${I18N_PREFIX}.${key}`, options)
}

/**
 * Whether the scope this rule carries needs a list of dishes or categories.
 *
 * Asked in one place because three components need the same answer — the form
 * shows the picker, the card summarises the reach, and the submit guard refuses
 * a scoped rule that names nothing.
 */
export function scopeNeedsTargets(scope: string): boolean {
  return scope === "items" || scope === "categories"
}

/** Which of the two catalogs the picker is choosing from. */
export function targetTypeForScope(scope: string): "items" | "categories" | null {
  if (scope === "items") return "items"
  if (scope === "categories") return "categories"
  return null
}

/** Which label the value field wears. A percentage is not money. */
export function promotionValueLabelKey(kind: string): string {
  return kind === "fixed_amount" ? "value_label_fixed" : "value_label_percentage"
}

/**
 * What the rule takes off, in one phrase.
 *
 * A percentage reads as a percentage and a fixed amount as money, for the same
 * reason `discountRequestLabel` splits them: a manager reading "20" with no unit
 * has no way to tell twenty percent from twenty pesos, and on a cheap round
 * those are wildly different rules.
 */
export function promotionValueLabel(
  promotion: Pick<Promotion, "kind" | "value">,
  currency: string
): string {
  if (promotion.kind === "fixed_amount") return formatMoney(promotion.value, { currency })

  const parsed = Number(promotion.value)

  return `${Number.isFinite(parsed) ? parsed : 0}%`
}

/**
 * Whether the form has everything the endpoint needs.
 *
 * A scoped rule with no targets is refused here rather than accepted and left to
 * reach nothing: the server takes it happily — a promotion that matches no line
 * changes no price — and a manager who saved one would watch a live rule do
 * nothing with no error to explain it.
 */
export function promotionRequestComplete(input: {
  name: string
  kind: string
  scope: string
  value: string
  targetIds: number[]
}): boolean {
  if (!input.name.trim()) return false
  if (!input.kind || !input.scope) return false
  if (scopeNeedsTargets(input.scope) && input.targetIds.length === 0) return false

  const parsed = Number(String(input.value).trim())

  return Number.isFinite(parsed) && parsed > 0
}

/**
 * How many days a code has left, or `null` when it never expires.
 *
 * Deliberately whole days measured from the instant, not a calendar
 * subtraction: the screen only ever uses it to decide between "expires soon" and
 * "expires", and a figure that flips at local midnight would disagree with the
 * server, which compares `expires_at` to the actual instant.
 */
export function daysUntil(expiresAt: string | null, now: Date = new Date()): number | null {
  if (!expiresAt) return null

  const target = new Date(expiresAt)
  if (Number.isNaN(target.getTime())) return null

  return Math.ceil((target.getTime() - now.getTime()) / 86_400_000)
}

/**
 * Whether a code is spent, in the sense the diner experiences.
 *
 * Not the same question as `status === "exhausted"`: a code whose count has
 * reached its ceiling is finished whether or not the row has been flipped yet,
 * and the row is only flipped by a redemption. A manager looking at 3/3 must not
 * be told the code still works.
 */
export function couponSpent(coupon: Pick<Coupon, "redemptionCount" | "maxRedemptions">): boolean {
  return coupon.redemptionCount >= coupon.maxRedemptions
}

/**
 * The tone for a coupon's status pill.
 *
 * `expired` and `revoked` are both dead, but they are not the same fact and the
 * label already says which — this only decides the mark. Kept off `danger` for
 * expiry, because a coupon reaching its own end date is the campaign working,
 * not a failure.
 */
export function couponTone(status: string): "available" | "disabled" | "danger" {
  if (status === "active") return "available"
  if (status === "revoked") return "danger"
  return "disabled"
}

/** The tone for a rule's status pill. `active` is the only one that costs money. */
export function promotionTone(status: string, liveNow: boolean): "available" | "reserved" | "disabled" {
  if (status !== "active") return "disabled"
  return liveNow ? "available" : "reserved"
}
